import { FunnelType } from "@/@types/Upsell";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { isValidHttpUrl, normalizeUrlOnBlur } from "@/utils/normalize";

export const useUpsellSchema = () => {
  const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;
  const { t } = useTranslation("upsells");

  const upsellSchema = z.object({
    name: z
      .string()
      .min(3, t("validation.minLength", { min: 3 }))
      .max(100, t("validation.maxLength", { max: 100 })),

    funnel_type: z.nativeEnum(FunnelType, {
      errorMap: () => ({ message: t("common:validation.invalidFunnelType") }),
    }),

    page: z
      .string()
      .min(1, t("validation.required"))
      .refine((v) => isValidHttpUrl(normalizeUrlOnBlur(v)), t("validation.invalidUrl")),

    offer_id: z.string().min(1, t("validation.required")),

    product_id: z.string().min(1, t("validation.required")),
    accepted_page: z
      .string()
      .refine((v) => isValidHttpUrl(normalizeUrlOnBlur(v)), t("validation.invalidUrl"))
      .optional()
      .or(z.literal("")),

    recused_page: z
      .string()
      .refine((v) => isValidHttpUrl(normalizeUrlOnBlur(v)), t("validation.invalidUrl"))
      .optional()
      .or(z.literal("")),

    up_sell_display_text: z
      .string()
      .min(1, t("common:validation.acceptButtonText.required"))
      .max(100, t("common:validation.acceptButtonText.tooLong")),

    up_sell_button_color: z
      .string()
      .regex(hexColorRegex, t("validation.invalidColor")),

    up_sell_display_text_color: z
      .string()
      .regex(hexColorRegex, t("validation.invalidColor")),

    down_sell_display_text: z
      .string()
      .min(1, t("common:validation.declineButtonText.required"))
      .max(100, t("common:validation.declineButtonText.tooLong")),

    down_sell_button_color: z
      .string()
      .regex(hexColorRegex, t("validation.invalidColor")),

    down_sell_display_text_color: z
      .string()
      .regex(hexColorRegex, t("validation.invalidColor")),
  });

  const editUpsellSchema = upsellSchema.partial();

  const getStepSchema = (step: number) => {
    switch (step) {
      case 1:
        return upsellSchema.pick({
          name: true,
          funnel_type: true,
          page: true,
        });
      case 2:
        return upsellSchema.pick({
          offer_id: true,
          product_id: true,
        });
      case 3:
        return upsellSchema.pick({
          accepted_page: true,
          recused_page: true,
        });
      case 4:
        return upsellSchema.pick({
          up_sell_display_text: true,
          up_sell_button_color: true,
          up_sell_display_text_color: true,
          down_sell_display_text: true,
          down_sell_button_color: true,
          down_sell_display_text_color: true,
        });
      default:
        return upsellSchema;
    }
  };

  return { upsellSchema, getStepSchema, editUpsellSchema };
};
